import chalk from "chalk";
import qs from "qs";
import { pick } from "lodash";
import { AxiosResponse, AxiosRequestConfig } from "axios";

import { isBrowser } from "../common/utils";
import { CtxType } from "../common/types";
import Logger from "../Logger";

import { IoResponse } from "./types";
const ioLogger = Logger.getNamespacedLogger("io", "info");

/**
 * 校验响应结果
 *  - 必须是一个对象
 *  - 是否存在 success 属性
 *  - success 为 false 时，必须存在 errCode 属性
 */
export function isValidResponse(obj: any): boolean {
  return (
    !!obj &&
    ["success"].every((k) => k in obj) &&
    (obj.success || (!obj.success && "errCode" in obj))
  );
}

/**
 * 创建错误的响应结构
 */
export function createErrorResponse(
  /**
   * 错误码
   */
  code: string,
  /**
   * 错误信息
   */
  message: string,
  statusCode?: number
): IoResponse {
  return {
    success: false,
    errCode: `${code}`,
    errMsg: `${message}`,
    $rawErrMsg: `${message}`,
    data: undefined,
    sysTime: 0,
    statusCode,
  };
}

/**
 * 需要打印的请求/响应头的信息
 */
const LOG_HEADER_LIST = [
  "dt",
  "vc",
  "ak-token",
  "cookie",
  "set-cookie",
  "referer",
  "user-agent",
  "content-type",
];

/**
 * 格式化请求信息，转换成字符串，用于打印
 */
export function formatRequestConfig(config: AxiosRequestConfig): string {
  return [
    chalk`{gray ${config.method || ""}}`,
    chalk`{red ${config.url || ""}}`,
    chalk`{cyan ${config.data ? stringify(config.data) : `''`}}`,
    chalk`{white ${JSON.stringify(pick(config.headers, LOG_HEADER_LIST))}}`,
  ].join(" ");
}

/**
 * 格式化响应信息，转换成字符串，用于打印
 */
export function formatResponse(response: AxiosResponse): string {
  return [
    chalk`{gray ${response.status + " " + response.statusText}}`,
    chalk`{cyan ${response.data ? convertResponseToString(response.data) : `''`}}`,
    chalk`{white ${JSON.stringify(pick(response.headers, LOG_HEADER_LIST))}}`,
  ].join(" ");
}

/**
 * 打印方法统一
 */
export function prettyLog({
  message,
  time,
  requestInfo,
  responseInfo,
  logLevel = "log",
}: {
  message: string;
  time: number | string;
  requestInfo: string;
  responseInfo: string;
  logLevel: "log" | "trace" | "debug" | "info" | "warn" | "error";
}): void {
  ioLogger[logLevel](
    [
      chalk`{blue ${message}}`,
      chalk`{gray ${time + "ms"}}`,
      chalk`{black ${requestInfo}}`,
      chalk`{cyan ${responseInfo}}`,
    ].join(chalk` {white ${"|"}} `)
  );
}

/**
 * 检测响应头中是否有 set-cookie 字段，有则进行应用到当前页面请求上
 */
export function checkAndAppendResponseSetCookie(ctx: CtxType, response: AxiosResponse): void {
  if (!isBrowser) {
    const remoteCookie = response.headers["set-cookie"];
    if (Array.isArray(remoteCookie)) {
      remoteCookie.forEach((content) => {
        ctx.res && ctx.res.append("Set-Cookie", content);
      });
    }
  }
}

/**
 * 转换响应数据为字符串
 */
function convertResponseToString(resp: string): string {
  if (!resp) {
    return resp + "";
  }
  if (typeof resp === "string") {
    return resp;
  }
  if (!isBrowser) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const util = require("util");
    return util.inspect(resp, { depth: 3, compact: true }).replace(/\s*\n\s*/g, " ");
  } else {
    try {
      return JSON.stringify(Object.assign({}, resp, { data: "*" }));
    } catch (err) {
      return resp + "";
    }
  }
}

/**
 * 数据进行 字符串格式化
 */
function stringify(data: string | Record<string, any>): string {
  let obj;
  if (typeof data === "string") {
    try {
      obj = qs.parse(data);
    } catch (e1) {
      try {
        obj = JSON.parse(data);
      } catch (e2) {
        // no stuff
      }
    }
  }
  if (!obj) {
    obj = data;
  }
  return typeof obj === "string" ? obj : JSON.stringify(obj);
}
